/**
 * Deactivate or reactivate a person everywhere: their admin app account and their web app
 * account, through the sign-in service's `POST /users/deactivate` or `POST /users/reactivate`.
 * Both switch the web app account first and leave the admin app account alone when that fails.
 * The server checks the permission and the "not yourself" rule again; hiding the button is only a
 * convenience.
 */
import { useRef, useState } from 'react';
import { useDataProvider, useInvalidate } from '@refinedev/core';
import { useMutation } from '@tanstack/react-query';
import { Button, Modal, message } from 'antd';
import {
  accountSwitchDoneMessage,
  accountSwitchErrorMessage,
  type AccountRow,
  type AccountSwitch,
  type AccountSwitchResult,
} from '../accounts';

const AUTH_DATA_PROVIDER = 'authDataProvider';

const COPY: Record<AccountSwitch, { verb: string; url: string; danger: boolean; explain: string }> =
  {
    deactivate: {
      verb: 'Deactivate',
      url: '/users/deactivate',
      danger: true,
      explain:
        'This switches off their admin app account and their web app account and signs them out of both. You can use Reactivate on this page to switch them back on.',
    },
    reactivate: {
      verb: 'Reactivate',
      url: '/users/reactivate',
      danger: false,
      explain:
        'This switches their admin app account and their web app account back on, if they were switched off. A suspended admin app account stays as it is. They sign in again to use them.',
    },
  };

interface AccountSwitchButtonProps {
  action: AccountSwitch;
  user: AccountRow;
  /** While the person's accounts are being checked again: no new switch on old information. */
  disabled?: boolean;
}

export const AccountSwitchButton: React.FC<AccountSwitchButtonProps> = ({
  action,
  user,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  // Set at once on confirm, before the mutation state re-renders, so a second click can't send
  // a second request.
  const busy = useRef(false);
  const dataProvider = useDataProvider();
  const invalidate = useInvalidate();
  const label = user.name || user.email;
  const copy = COPY[action];

  const mutation = useMutation({
    mutationFn: async (email: string) => {
      const { custom } = dataProvider(AUTH_DATA_PROVIDER);
      if (!custom) throw new Error('The sign-in service client cannot send this request.');
      const { data } = await custom({ url: copy.url, method: 'post', payload: { email } });
      return data as AccountSwitchResult;
    },
    onSuccess: (result) => {
      setOpen(false);
      message.success(accountSwitchDoneMessage(action, label, result));
      // Re-reads the person and their web app account, so the right buttons show.
      invalidate({
        resource: 'users',
        dataProviderName: AUTH_DATA_PROVIDER,
        invalidates: ['resourceAll'],
      });
    },
    onError: (error) => {
      message.error(`Could not ${action} ${label}: ${accountSwitchErrorMessage(error)}`);
    },
    onSettled: () => {
      busy.current = false;
    },
  });
  const running = mutation.isLoading;

  const confirm = () => {
    if (busy.current || disabled) return;
    busy.current = true;
    mutation.mutate(user.email);
  };

  return (
    <>
      <Button
        danger={copy.danger}
        aria-label={`${copy.verb} ${label}`}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        {copy.verb}
      </Button>
      <Modal
        open={open}
        title={`${copy.verb} ${label}?`}
        okText={copy.verb}
        okButtonProps={{ danger: copy.danger, disabled: disabled && !running }}
        confirmLoading={running}
        cancelButtonProps={{ disabled: running }}
        closable={!running}
        maskClosable={!running}
        onOk={confirm}
        onCancel={() => {
          if (!running) setOpen(false);
        }}
      >
        {copy.explain}
      </Modal>
    </>
  );
};
